import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Check as SubmitIcon,
} from '@mui/icons-material';

// API hooks
import { 
  useGetPresentationByIdQuery,
  useSubmitVoteMutation,
  useGetVotesQuery,
} from '../../services/api';

// Redux
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setUserVote } from '../../services/slices/votingSlice';
import { Question } from '../../services/slices/presentationSlice';

const ActiveVoting: React.FC = () => {
  const { presentationId } = useParams<{ presentationId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, number>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Redux state
  const { user } = useAppSelector((state) => state.auth);
  const { userVotes } = useAppSelector((state) => state.voting);
  
  // Fetch presentation
  const { 
    data: presentation, 
    isLoading: isLoadingPresentation, 
    error: presentationError 
  } = useGetPresentationByIdQuery(presentationId || '');
  
  // Submit vote mutation
  const [submitVote, { isLoading: isSubmitting }] = useSubmitVoteMutation();
  
  // Sort questions by order (create a copy first to avoid mutating the original array)
  const sortedQuestions = presentation?.questions ? [...presentation.questions].sort((a: Question, b: Question) => a.order - b.order) : [];
  
  // Initialize selected options from user votes
  useEffect(() => {
    if (presentation && userVotes) {
      const initialOptions: Record<string, number> = {};
      presentation.questions.forEach((question: Question) => {
        if (userVotes[question.id]) {
          initialOptions[question.id] = userVotes[question.id].optionIndex;
        }
      });
      setSelectedOptions(initialOptions);
    }
  }, [presentation, userVotes]);
  
  if (isLoadingPresentation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (presentationError || !presentation) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          Error loading presentation. Please try again later.
        </Alert>
      </Container>
    );
  }
  
  if (presentation.status !== 'active') {
    return (
      <Container maxWidth="md">
        <Alert severity="info" sx={{ mt: 4 }}>
          This presentation is not currently active for voting.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/presentations')}
          sx={{ mt: 2 }}
        >
          Back to Presentations
        </Button>
      </Container>
    );
  }
  
  const currentQuestion = sortedQuestions[activeStep];
  
  const handleOptionChange = (questionId: string, optionIndex: number) => {
    setSelectedOptions(prev => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };
  
  const handleNext = () => {
    if (activeStep < sortedQuestions.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };
  
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };
  
  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };
  
  const handleSubmitVote = async (questionId: string) => {
    if (selectedOptions[questionId] === undefined) {
      setSnackbarMessage('Please select an option before submitting');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const result = await submitVote({
        presentationId: presentation.id,
        questionId,
        optionIndex: selectedOptions[questionId],
      }).unwrap();
      
      // Dispatch to store
      if (result.vote) {
        dispatch(setUserVote(result.vote));
      }
      
      setSnackbarMessage('Vote submitted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Move to next question if available
      if (activeStep < sortedQuestions.length - 1) {
        setTimeout(() => {
          handleNext();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to submit vote:', error);
      setSnackbarMessage('Failed to submit vote. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };
  
  const isQuestionAnswered = (questionId: string) => {
    return selectedOptions[questionId] !== undefined;
  };
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/presentations/${presentation.id}`, { replace: true })}
          sx={{ mb: 2 }}
        >
          Back to Presentation
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {presentation.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Presenter: {presentation.presenter}
        </Typography>
      </Box>
      
      {/* Question Stepper */}
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel 
        nonLinear
        sx={{ mb: 4 }}
      >
        {sortedQuestions.map((question: Question, index: number) => (
          <Step key={question.id} completed={isQuestionAnswered(question.id)}>
            <StepButton onClick={() => handleStepClick(index)}>
              Question {index + 1}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      
      {/* Current Question */}
      {currentQuestion && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Question {activeStep + 1} of {sortedQuestions.length}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {currentQuestion.text}
            </Typography>
            
            <Paper sx={{ p: 3, mt: 2, mb: 3 }}>
              <RadioGroup
                value={selectedOptions[currentQuestion.id] ?? ''}
                onChange={(e) => handleOptionChange(
                  currentQuestion.id, 
                  parseInt(e.target.value, 10)
                )}
              >
                {currentQuestion.options.map((option: string, index: number) => (
                  <FormControlLabel
                    key={index}
                    value={index}
                    control={<Radio />}
                    label={option}
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<BackIcon />}
              >
                Previous
              </Button>
              
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmitVote(currentQuestion.id)}
                  disabled={isSubmitting || selectedOptions[currentQuestion.id] === undefined}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <SubmitIcon />}
                  sx={{ mr: 1 }}
                >
                  {isQuestionAnswered(currentQuestion.id) ? 'Update Vote' : 'Submit Vote'}
                </Button>
                
                {activeStep < sortedQuestions.length - 1 && (
                  <Button
                    variant="outlined"
                    onClick={handleNext}
                    endIcon={<NextIcon />}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Progress Summary */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Progress
        </Typography>
        <Typography variant="body1">
          {Object.keys(selectedOptions).length} of {sortedQuestions.length} questions answered
        </Typography>
        <Box sx={{ mt: 2 }}>
          {sortedQuestions.map((question: Question, index: number) => (
            <Button
              key={question.id}
              variant={isQuestionAnswered(question.id) ? "contained" : "outlined"}
              color={isQuestionAnswered(question.id) ? "success" : "primary"}
              size="small"
              onClick={() => handleStepClick(index)}
              sx={{ mr: 1, mb: 1 }}
            >
              Q{index + 1}
            </Button>
          ))}
        </Box>
      </Paper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ActiveVoting;
